import React from 'react';
import { ChevronUp, ChevronDown, ChevronRight, ChevronLeft, Lock, Unlock } from 'lucide-react';
import { colCellStyle } from '../hooks/useTableColumnLayout';

// ============================================================================
// TableColHeader — a resizable / collapsible / freezable / draggable <th>
// ============================================================================
// The HTML-table counterpart of BoqGridHeader, driven by useTableColumnLayout.
// Matches the Team-Overview header treatment: a small grey uppercase label
// with a gold sort caret, freeze (lock) + collapse tools that reveal on hover,
// a right-edge resize handle, and native drag-to-reorder.
// ============================================================================

const GOLD = '#9f902a';
const GREY = '#666666';

export default function TableColHeader({
  col, meta, sortDir, onSort, onToggleCollapse, onToggleFreeze, startResize, onDragStart, onDrop,
}) {
  const baseTh = {
    padding: '0.5rem', color: GREY, fontWeight: 600, fontSize: '0.82rem',
    // Sticky to the top of the table's scroll box, so the header row stays in
    // view while the rows scroll. Also the positioned parent the resize
    // handle needs. A frozen column adds its sticky left on top of this.
    position: 'sticky', top: 0, zIndex: 3, background: 'var(--brio-surface-muted, #f5f5f5)',
    textAlign: col.align || 'left', whiteSpace: 'nowrap', boxSizing: 'border-box',
  };
  const style = { ...baseTh, ...colCellStyle(meta, { isHeader: true }) };

  // Collapsed: just the expand chevron (always visible — the only way back).
  if (meta.isCollapsed) {
    return (
      <th style={{ ...style, padding: 0, cursor: 'pointer' }} title={`Expand ${col.header || col.id}`}
        onClick={() => onToggleCollapse(col.id)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChevronRight size={12} strokeWidth={2.5} style={{ color: GOLD }} />
        </div>
      </th>
    );
  }

  const sortable = col.sortable !== false && !!col.sortField;
  const showTools = col.freezable !== false || col.collapsible !== false;

  return (
    <th
      style={style}
      draggable
      onDragStart={() => onDragStart(col.id)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => onDrop(col.id)}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: col.align === 'right' ? 'flex-end' : 'space-between' }}>
        <span
          onClick={sortable ? () => onSort(col.sortField) : undefined}
          style={{ cursor: sortable ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 2, overflow: 'hidden' }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.header}</span>
          {sortDir === 'asc' && <ChevronUp size={12} style={{ color: GOLD, flexShrink: 0 }} />}
          {sortDir === 'desc' && <ChevronDown size={12} style={{ color: GOLD, flexShrink: 0 }} />}
        </span>
        {showTools && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
            {col.freezable !== false && (
              <span
                className={meta.isFrozen ? undefined : 'boq-col-header__tool'}
                onClick={(e) => { e.stopPropagation(); onToggleFreeze(col.id); }}
                title={meta.isFrozen ? 'Unfreeze column' : 'Freeze column'}
                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: meta.isFrozen ? GOLD : GREY, opacity: meta.isFrozen ? 1 : 0.5 }}
              >
                {meta.isFrozen ? <Lock size={11} /> : <Unlock size={11} />}
              </span>
            )}
            {col.collapsible !== false && (
              <span
                className="boq-col-header__tool"
                onClick={(e) => { e.stopPropagation(); onToggleCollapse(col.id); }}
                title="Collapse column"
                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: GREY }}
              >
                <ChevronLeft size={12} />
              </span>
            )}
          </span>
        )}
      </span>
      {/* Right-edge resize handle. */}
      <span
        onMouseDown={(e) => startResize(col.id, e)}
        title="Drag to resize"
        style={{ position: 'absolute', top: 0, right: 0, width: 6, height: '100%', cursor: 'col-resize', userSelect: 'none' }}
      />
    </th>
  );
}
